import styled from "@emotion/styled"
import type { ActionItem as ActionItemType, ModelInformationView, TechnicalDataView } from "@slfinrtl/ui-bff-routes"
import { useEffect, useMemo, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { ExteriorColorTile, Flex, InteriorColorTile, List, Skeleton } from "src/components"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import type { ColorsProps } from "../../../hooks/useColors/useColors"
import { EditButton } from "../editButton/EditButton"

const messages = defineMessages({
	driveType: {
		defaultMessage: "Drive Type",
		id: "vehicleDetails.overview.summary.driveType",
	},
	exteriorColor: {
		defaultMessage: "Exterior Color",
		id: "vehicleDetails.overview.summary.exteriorColor",
	},
	interiorColor: {
		defaultMessage: "Interior Color",
		id: "vehicleDetails.overview.summary.interiorColor",
	},
	modelGeneration: {
		defaultMessage: "Model Generation",
		id: "vehicleDetails.overview.summary.modelGeneration",
	},
	modelInformationTitle: {
		defaultMessage: "Model Information",
		id: "vehicleDetails.overview.summary.modelInformationTitle",
	},
	power: {
		defaultMessage: "Power",
		id: "vehicleDetails.overview.summary.power",
	},
	roofColor: {
		defaultMessage: "Roof Color",
		id: "vehicleDetails.roofColor",
	},
})

interface ModelInformationListProps {
	colors: ColorsProps | null
	colorsEdit?: ActionItemType
}

export const ModelInformationList = ({ colors, colorsEdit }: ModelInformationListProps) => {
	const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()
	const [technicalDataView, setTechnicalDataView] = useState<TechnicalDataView | null>()
	const { formatMessage } = useIntl()
	const { subscribe } = useVehicleDetailsContext()

	useEffect(() => {
		subscribe("modelInformationChanged", setModelInformationView)
		subscribe("technicalDataChanged", setTechnicalDataView)
	}, [subscribe])

	const modelGenerationAndYear = useMemo(
		() =>
			modelInformationView
				? `${modelInformationView.modelGeneration} (${modelInformationView.modelYear})`
				: modelInformationView,
		[modelInformationView],
	)
	const driveTrainAndTransmission = useMemo(
		() =>
			modelInformationView
				? `${modelInformationView.driveTrain} / ${modelInformationView.transmission}`
				: modelInformationView,
		[modelInformationView],
	)

	if (colors === null && modelInformationView === null && technicalDataView === null) {
		return null
	}

	return (
		<List header={formatMessage(messages.modelInformationTitle)}>
			{modelGenerationAndYear !== null && (
				<List.Item
					label={formatMessage(messages.modelGeneration)}
					value={modelGenerationAndYear || <StyledSkeletonText />}
					valueSingleLine
				/>
			)}
			{driveTrainAndTransmission !== null && (
				<List.Item
					label={formatMessage(messages.driveType)}
					value={driveTrainAndTransmission || <StyledSkeletonText />}
					valueSingleLine
				/>
			)}
			{technicalDataView !== null && (
				<List.Item
					label={technicalDataView?.powerField?.label ?? formatMessage(messages.power)}
					value={
						technicalDataView ? (
							(technicalDataView.powerField?.value ?? technicalDataView.power)
						) : technicalDataView === null ? (
							emptyPlaceholder
						) : (
							<StyledSkeletonText />
						)
					}
					valueSingleLine
				/>
			)}
			{colors !== null && (
				<List.Item
					label={formatMessage(messages.exteriorColor)}
					value={
						colors.exteriorColor ? (
							<Flex $gap="xs">
								{colors.exteriorColor.hexCode && (
									<ExteriorColorTile color={colors.exteriorColor.hexCode} />
								)}
								<List.ItemValue value={colors.exteriorColor.name ?? emptyPlaceholder} />
								{colorsEdit && (
									<EditButton
										actionItem={colorsEdit}
										onClick={colors.exteriorColor.showExteriorColorModal}
									/>
								)}
							</Flex>
						) : (
							<StyledSkeletonText />
						)
					}
					valueSingleLine
				/>
			)}

			{colors?.roofColor && (
				<List.Item
					label={formatMessage(messages.roofColor)}
					value={
						<Flex $gap="xs">
							{colors.roofColor.hexCode && (
								<ExteriorColorTile glossy={false} color={colors.roofColor.hexCode} />
							)}
							<List.ItemValue value={colors.roofColor.name ?? emptyPlaceholder} />
							{colorsEdit && (
								<EditButton actionItem={colorsEdit} onClick={colors.roofColor.showRoofColorModal} />
							)}
						</Flex>
					}
					valueSingleLine
				/>
			)}
			{colors !== null && (
				<List.Item
					label={formatMessage(messages.interiorColor)}
					value={
						colors.interior ? (
							<Flex $gap="xs">
								{colors.interior.color && (
									<InteriorColorTile
										seatColor={colors.interior.color.seatColorHexCode}
										seatStitchingColor={colors.interior.color.seatStitchingColorHexCode}
										cockpitColor={colors.interior.color.cockpitColorHexCode}
										cockpitStitchingColor={colors.interior.color.cockpitStitchingColorHexCode}
									/>
								)}
								<List.ItemValue value={colors.interior.name ?? emptyPlaceholder} />
								{colorsEdit && (
									<EditButton actionItem={colorsEdit} onClick={colors.interior.showInteriorModal} />
								)}
							</Flex>
						) : (
							<StyledSkeletonText />
						)
					}
					valueSingleLine
				/>
			)}
		</List>
	)
}

const StyledSkeletonText = styled(Skeleton.Text)`
	width: 60%;
`
